import { Info } from 'lucide-react';

import { tierBands, weightPct } from './format';

import { cn } from '@/lib/cn';
import type { ReadinessFormula } from '@/lib/api/readiness';


export interface ReadinessFormulaSummaryProps {
  formula?: ReadinessFormula | null;
  isLoading?: boolean;
  className?: string;
}

/**
 * Plain-language description of how the readiness score is built, generated
 * entirely from the API's formula definition.
 *
 * This replaces the old static sentence in the org portal, which claimed the
 * score used "mock performance" (an input that has never existed) and omitted
 * curriculum progress. Copy that restates weights by hand always drifts; this
 * cannot.
 */
export function ReadinessFormulaSummary({
  formula,
  isLoading,
  className,
}: ReadinessFormulaSummaryProps) {
  if (!formula) {
    return (
      <div
        className={cn(
          'flex items-start gap-3 rounded-[16px] border border-border bg-card px-5 py-4',
          className,
        )}
      >
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#2563eb]" />
        <p className="text-sm text-muted-foreground">
          {isLoading
            ? 'Loading how the readiness score is calculated…'
            : 'How the readiness score is calculated is temporarily unavailable.'}
        </p>
      </div>
    );
  }

  const bands = tierBands(formula);

  return (
    <section
      aria-label="How the readiness score is calculated"
      className={cn('rounded-[16px] border border-border bg-card px-5 py-4', className)}
    >
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#2563eb]" />
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">
            How the readiness score is calculated
            <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              v{formula.version}
            </span>
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{formula.summary}</p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {formula.pillars.map((pillar) => (
          <div key={pillar.key} className="rounded-[12px] bg-muted/50 p-3.5">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">{pillar.label}</h3>
              <span className="text-sm font-bold tabular-nums text-foreground">
                {weightPct(pillar.weight)}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{pillar.description}</p>
            <ul className="mt-2.5 space-y-1.5">
              {pillar.components.map((c) => (
                <li key={c.key} className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="min-w-0 text-muted-foreground" title={c.description}>
                    {c.label}
                  </span>
                  <span className="flex-none tabular-nums text-foreground">
                    {weightPct(pillar.weight * c.weight)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Bands
        </h3>
        <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          {bands.map((b) => (
            <li key={b.key} className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{b.label}</span>{' '}
              <span className="tabular-nums">{b.range}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        {formula.renormalisation_note}
      </p>
    </section>
  );
}
